import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../utils/Functions';
import TopBar from '../components/TopBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const RegisterPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('register'); // 'register' or 'otp'
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let finalValue = value;
    
    if (name === 'name') {
      // Only allow alphabetic characters and spaces
      finalValue = value.replace(/[^a-zA-Z\s]/g, '');
    } else if (name === 'phone') {
      // Only allow digits and an optional leading plus
      const cleaned = value.replace(/(?!^)\+/g, '').replace(/[^\d+]/g, '');
      const maxLen = cleaned.startsWith('+') ? 15 : 10;
      if (cleaned.length > maxLen) return;
      finalValue = cleaned;
    }

    setForm((prev) => ({ ...prev, [name]: finalValue }));
    // Clear field-level error on change
    if (fieldErrors[name]) {
      setFieldErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const res = await response.json();

      if (!response.ok) {
        if (res.errors) {
          // Laravel validation errors — each key maps to an array of messages
          const flat = {};
          Object.keys(res.errors).forEach((key) => {
            flat[key] = res.errors[key][0];
          });
          setFieldErrors(flat);
        } else {
          setError(res.message || 'Registration failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      setSuccess('Registration successful! A verification OTP has been sent to your email.');
      setLoading(false);
      setStep('otp');
    } catch {
      setError('Unable to connect to the server. Please try again.');
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setVerifying(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email_or_phone: form.email,
          otp: otp.join(''),
          purpose: 'register_verification'
        }),
      });

      const res = await response.json();

      if (!response.ok) {
        setError(res.message || 'Invalid or expired OTP.');
        setVerifying(false);
        return;
      }

      setSuccess('Email verified successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      setError('Unable to connect to the server. Please try again.');
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setSuccess('');
    setResending(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email_or_phone: form.email,
          purpose: 'register_verification'
        }),
      });

      const res = await response.json();

      if (!response.ok) {
        setError(res.message || 'Failed to resend OTP.');
        setResending(false);
        return;
      }

      setSuccess('A new verification OTP has been sent to your email.');
      setResending(false);
    } catch {
      setError('Unable to connect to the server. Please try again.');
      setResending(false);
    }
  };

  return (
    <>
      <TopBar />
      <Navbar />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        .reg-wrapper {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', sans-serif;
          background: #f5f7fa;
          position: relative;
          overflow: hidden;
        }

        /* Background shapes */
        .reg-bg-shape {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(70px);
          opacity: 0.45;
          animation: regShapeFloat 10s ease-in-out infinite alternate;
        }
        .reg-bg-shape-1 {
          width: 520px; height: 520px;
          background: radial-gradient(circle, #a7f3d0, transparent 70%);
          top: -160px; right: -80px;
        }
        .reg-bg-shape-2 {
          width: 380px; height: 380px;
          background: radial-gradient(circle, #c7d2fe, transparent 70%);
          bottom: -80px; left: -80px;
          animation-delay: -5s;
        }
        .reg-bg-shape-3 {
          width: 250px; height: 250px;
          background: radial-gradient(circle, #bbf7d0, transparent 70%);
          top: 40%; left: 28%;
          animation-delay: -2s;
        }
        @keyframes regShapeFloat {
          0%   { transform: translate(0, 0) scale(1); }
          100% { transform: translate(15px, -20px) scale(1.04); }
        }

        /* Dot grid */
        .reg-bg-grid {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(99,102,241,0.07) 1px, transparent 1px);
          background-size: 36px 36px;
          pointer-events: none;
        }

        /* ── Left Panel ── */
        .reg-left-panel {
          flex: 1;
          display: flex; flex-direction: column; justify-content: space-between;
          padding: 48px 60px;
          position: relative; z-index: 1; overflow: hidden;
        }
        .reg-left-panel::before {
          content: ''; position: absolute; inset: 0;
          background: url('https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80') center/cover no-repeat;
          z-index: -2;
        }
        .reg-left-panel::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(160deg, rgba(6, 24, 48,0.84) 0%, rgba(6, 24, 48,0.76) 50%, rgba(207, 160, 71,0.62) 100%);
          z-index: -1;
        }
        .reg-left-content {
          max-width: 480px; width: 100%;
          position: relative; z-index: 2;
          display: flex; flex-direction: column; height: 100%; justify-content: space-between;
        }
        .reg-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 64px; }
        .reg-brand-icon {
          width: 44px; height: 44px;
          background: rgba(255,255,255,0.15);
          border: 1.5px solid rgba(255,255,255,0.35);
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; color: white;
        }
        .reg-brand-text { font-size: 22px; font-weight: 800; color: #fff; letter-spacing: -0.3px; }

        .reg-hero-tag {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.95); font-size: 11px; font-weight: 700;
          letter-spacing: 1.4px; text-transform: uppercase;
          padding: 6px 16px; border-radius: 50px; margin-bottom: 28px;
        }
        .reg-hero-tag-dot {
          width: 6px; height: 6px; background: #CFA047;
          border-radius: 50%; box-shadow: 0 0 8px rgba(207, 160, 71,0.8);
        }
        .reg-hero-title {
          font-size: 46px; font-weight: 800; color: #fff;
          line-height: 1.12; letter-spacing: -2px; margin: 0 0 20px 0;
        }
        .reg-hero-title span { color: #CFA047; }
        .reg-hero-desc { font-size: 15px; color: rgba(255,255,255,0.78); line-height: 1.75; margin-bottom: 48px; max-width: 400px; }
        .reg-features { display: flex; flex-direction: column; gap: 14px; }
        .reg-feature-item { display: flex; align-items: center; gap: 14px; }
        .reg-feature-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.22);
          display: flex; align-items: center; justify-content: center;
          color: #CFA047; font-size: 14px; flex-shrink: 0;
        }
        .reg-feature-text { font-size: 14px; font-weight: 500; color: rgba(255,255,255,0.88); }

        /* ── Right Panel ── */
        .reg-right-panel {
          width: 560px; min-width: 560px;
          background: #ffffff;
          display: flex; align-items: center; justify-content: center;
          padding: 36px 48px;
          position: relative; z-index: 1;
          border: 3px solid #061830;
          border-radius: 4px;
          overflow-y: auto;
          overflow-x: hidden;
          box-sizing: border-box;
        }

        .reg-watermark {
          position: absolute; color: #061830; opacity: 0.045;
          pointer-events: none; user-select: none; line-height: 1;
        }

        @media (max-width: 960px) {
          .reg-left-panel { display: none; }
          .reg-right-panel { width: 100%; min-width: 100%; }
        }

        .reg-card { width: 100%; max-width: 440px; }
        .reg-card-header { margin-bottom: 24px; text-align: center; }
        .reg-card-title {
          font-size: 30px; font-weight: 800; color: #0f172a;
          margin: 0 0 4px 0; letter-spacing: -1px; line-height: 1.1;
        }
        .reg-title-bar {
          width: 48px; height: 4px;
          background: linear-gradient(90deg, #061830, #CFA047);
          border-radius: 4px; margin: 12px auto 14px auto;
        }
        .reg-card-subtitle { font-size: 14px; color: #64748b; line-height: 1.6; }

        /* Two-column row */
        .reg-form-row-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* Form groups */
        .reg-form-group { margin-bottom: 14px; }
        .reg-form-label {
          display: block; margin-bottom: 7px;
          font-weight: 700; font-size: 11.5px; color: #1e293b;
          letter-spacing: 0.4px; text-transform: uppercase;
        }
        .reg-input-wrap { position: relative; }
        .reg-input-icon {
          position: absolute; left: 16px; top: 50%;
          transform: translateY(-50%);
          color: #94a3b8; font-size: 13px; pointer-events: none;
          transition: color 0.2s;
        }
        .reg-input-wrap:focus-within .reg-input-icon { color: #061830; }
        .reg-form-input {
          width: 100%;
          padding: 13px 16px 13px 44px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 11px;
          font-size: 14.5px; color: #0f172a;
          font-family: 'Inter', sans-serif;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .reg-form-input::placeholder { color: #cbd5e1; }
        .reg-form-input:focus {
          outline: none;
          border-color: #061830;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(6, 24, 48,0.08), 0 2px 8px rgba(0,0,0,0.05);
        }
        .reg-form-input.input-error { border-color: #f87171; background: #fff5f5; }
        .reg-field-error { font-size: 12px; color: #b91c1c; margin-top: 5px; display: flex; align-items: center; gap: 5px; }

        /* Password toggle */
        .reg-pass-toggle {
          position: absolute; right: 14px; top: 50%;
          transform: translateY(-50%);
          color: #94a3b8; background: none; border: none;
          cursor: pointer; padding: 0; font-size: 13px;
          transition: color 0.18s;
        }
        .reg-pass-toggle:hover { color: #061830; }

        /* Error banner */
        .reg-error-banner {
          display: flex; align-items: flex-start; gap: 10px;
          background: #fff5f5; border: 1.5px solid #fca5a5;
          border-radius: 10px; padding: 12px 16px;
          margin-bottom: 18px; font-size: 13px; color: #b91c1c; line-height: 1.5;
          animation: regBannerIn 0.25s ease;
        }
        .reg-error-banner i { margin-top: 2px; flex-shrink: 0; }

        /* Success banner */
        .reg-success-banner {
          display: flex; align-items: flex-start; gap: 10px;
          background: #f0fdf4; border: 1.5px solid #86efac;
          border-radius: 10px; padding: 12px 16px;
          margin-bottom: 18px; font-size: 13px; color: #15803d; line-height: 1.5;
          animation: regBannerIn 0.25s ease;
        }
        .reg-success-banner i { margin-top: 2px; flex-shrink: 0; }

        @keyframes regBannerIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Submit button */
        .reg-submit-btn {
          width: 100%; padding: 15px;
          background: linear-gradient(135deg, #061830 0%, #061830 100%);
          border: none; border-radius: 50px;
          color: white; font-size: 15px; font-weight: 700;
          cursor: pointer; transition: all 0.25s;
          font-family: 'Inter', sans-serif;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          box-shadow: 0 10px 28px rgba(6, 24, 48,0.30);
          letter-spacing: 0.3px; margin-top: 6px;
        }
        .reg-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 16px 36px rgba(6, 24, 48,0.42);
        }
        .reg-submit-btn:active:not(:disabled) { transform: scale(0.98); }
        .reg-submit-btn:disabled { opacity: 0.75; cursor: not-allowed; }

        .reg-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white; border-radius: 50%;
          animation: regSpin 0.7s linear infinite;
        }
        @keyframes regSpin { to { transform: rotate(360deg); } }

        /* Divider */
        .reg-divider {
          display: flex; align-items: center; gap: 12px;
          margin: 24px 0 16px;
        }
        .reg-divider-line { flex: 1; height: 1px; background: #f1f5f9; }
        .reg-divider-text { font-size: 11px; color: #94a3b8; white-space: nowrap; font-weight: 600; letter-spacing: 0.5px; }

        /* Login link */
        .reg-login-link { text-align: center; font-size: 13.5px; color: #64748b; }
        .reg-login-link a { color: #061830; font-weight: 700; text-decoration: none; }
        .reg-login-link a:hover { color: #061830; text-decoration: underline; }

        /* Password strength */
        .reg-strength-bar {
          margin-top: 7px;
          display: flex; gap: 4px;
        }
        .reg-strength-seg {
          height: 3px; flex: 1; border-radius: 4px;
          background: #e2e8f0;
          transition: background 0.3s;
        }
        .reg-strength-label { font-size: 11px; margin-top: 4px; font-weight: 600; }
      `}</style>

      <div className="reg-wrapper">
        <div className="reg-bg-grid"></div>
        <div className="reg-bg-shape reg-bg-shape-1"></div>
        <div className="reg-bg-shape reg-bg-shape-2"></div>
        <div className="reg-bg-shape reg-bg-shape-3"></div>

        {/* Left Panel */}
        <div className="reg-left-panel">
          <div className="reg-left-content">
            <div className="reg-brand">
              <img src="/images/logo.png" alt="Logo" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
            </div>

            <div>
              <div className="reg-hero-tag">
                <span className="reg-hero-tag-dot"></span>
                Create Your Account
              </div>
              <h1 className="reg-hero-title">
                Join the<br />
                Legal Infos<br />
                Platform <span>Today</span>
              </h1>
              <p className="reg-hero-desc">
                Register to access a powerful suite of tax filing tools,
                real-time analytics, and expert support — all in one secure place.
              </p>
            </div>

            <div className="reg-features">
              {[
                { icon: 'fa-shield-halved', text: 'Your data is encrypted and secure' },
                { icon: 'fa-file-invoice-dollar', text: 'Smart tax filing made simple' },
                { icon: 'fa-headset', text: '24/7 expert support at your fingertips' },
              ].map((f, i) => (
                <div key={i} className="reg-feature-item">
                  <div className="reg-feature-icon">
                    <i className={`fa-solid ${f.icon}`}></i>
                  </div>
                  <span className="reg-feature-text">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="reg-right-panel">
          <i className="fa-solid fa-building-columns reg-watermark" style={{ fontSize: '120px', top: '-20px', right: '-20px', transform: 'rotate(15deg)' }}></i>
          <i className="fa-solid fa-user-plus reg-watermark" style={{ fontSize: '90px', bottom: '30px', left: '-15px', transform: 'rotate(-20deg)' }}></i>
          <i className="fa-solid fa-shield-halved reg-watermark" style={{ fontSize: '70px', top: '40%', right: '20px', transform: 'rotate(10deg)' }}></i>
          <i className="fa-solid fa-scale-balanced reg-watermark" style={{ fontSize: '55px', bottom: '80px', right: '40px', transform: 'rotate(20deg)' }}></i>

          <div className="reg-card">
            <div className="reg-card-header">
              <h2 className="reg-card-title">{step === 'register' ? 'Create Account' : 'Verify Email'}</h2>
              <div className="reg-title-bar"></div>
              <p className="reg-card-subtitle">{step === 'register' ? 'Fill in the details below to register your account' : 'Enter the 6-digit OTP sent to your email'}</p>
            </div>

            {error && (
              <div className="reg-error-banner" role="alert">
                <i className="fa-solid fa-circle-exclamation"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="reg-success-banner" role="alert">
                <i className="fa-solid fa-circle-check"></i>
                <span>{success}</span>
              </div>
            )}

            {step === 'register' ? (
              <form onSubmit={handleRegister} noValidate>
                {/* Name */}
                <div className="reg-form-group">
                <label className="reg-form-label" htmlFor="reg-name">Full Name</label>
                <div className="reg-input-wrap">
                  <i className="fa-solid fa-user reg-input-icon"></i>
                  <input
                    id="reg-name"
                    name="name"
                    type="text"
                    className={`reg-form-input${fieldErrors.name ? ' input-error' : ''}`}
                    placeholder="John Doe"
                    value={form.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                  />
                </div>
                {fieldErrors.name && (
                  <div className="reg-field-error">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    {fieldErrors.name}
                  </div>
                )}
              </div>

              {/* Email & Phone — two columns */}
              <div className="reg-form-row-2">
                <div className="reg-form-group">
                  <label className="reg-form-label" htmlFor="reg-email">Email Address</label>
                  <div className="reg-input-wrap">
                    <i className="fa-solid fa-envelope reg-input-icon"></i>
                    <input
                      id="reg-email"
                      name="email"
                      type="email"
                      className={`reg-form-input${fieldErrors.email ? ' input-error' : ''}`}
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </div>
                  {fieldErrors.email && (
                    <div className="reg-field-error">
                      <i className="fa-solid fa-triangle-exclamation"></i>
                      {fieldErrors.email}
                    </div>
                  )}
                </div>

                <div className="reg-form-group">
                  <label className="reg-form-label" htmlFor="reg-phone">Phone Number</label>
                  <div className="reg-input-wrap">
                    <i className="fa-solid fa-phone reg-input-icon"></i>
                    <input
                      id="reg-phone"
                      name="phone"
                      type="tel"
                      className={`reg-form-input${fieldErrors.phone ? ' input-error' : ''}`}
                      placeholder="+91 8608680756"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                    />
                  </div>
                  {fieldErrors.phone && (
                    <div className="reg-field-error">
                      <i className="fa-solid fa-triangle-exclamation"></i>
                      {fieldErrors.phone}
                    </div>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className="reg-form-group">
                <label className="reg-form-label" htmlFor="reg-password">Password</label>
                <div className="reg-input-wrap">
                  <i className="fa-solid fa-lock reg-input-icon"></i>
                  <input
                    id="reg-password"
                    name="password"
                    type={showPass ? 'text' : 'password'}
                    className={`reg-form-input${fieldErrors.password ? ' input-error' : ''}`}
                    placeholder="Min. 8 characters"
                    value={form.password}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                  />
                  <button type="button" className="reg-pass-toggle" onClick={() => setShowPass(!showPass)} tabIndex={-1}>
                    <i className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                {/* Password strength indicator */}
                <PasswordStrength password={form.password} />
                {fieldErrors.password && (
                  <div className="reg-field-error">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    {fieldErrors.password}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="reg-form-group">
                <label className="reg-form-label" htmlFor="reg-confirm-password">Confirm Password</label>
                <div className="reg-input-wrap">
                  <i className="fa-solid fa-lock-open reg-input-icon"></i>
                  <input
                    id="reg-confirm-password"
                    name="password_confirmation"
                    type={showConfirm ? 'text' : 'password'}
                    className={`reg-form-input${fieldErrors.password_confirmation ? ' input-error' : ''}`}
                    placeholder="Re-enter password"
                    value={form.password_confirmation}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                  />
                  <button type="button" className="reg-pass-toggle" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                    <i className={`fa-solid ${showConfirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                {fieldErrors.password_confirmation && (
                  <div className="reg-field-error">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    {fieldErrors.password_confirmation}
                  </div>
                )}
              </div>

              <button
                  type="submit"
                  id="reg-submit"
                  className="reg-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="reg-spinner"></span>
                      Creating Account…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-user-plus"></i>
                      Create Account
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} noValidate>
                <div className="reg-form-group">
                  <label className="reg-form-label" style={{ textAlign: 'center', marginBottom: '12px' }}>One-Time Password (OTP)</label>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '8px' }}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        className="reg-form-input"
                        value={digit}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          if (val) {
                            const newOtp = [...otp];
                            newOtp[index] = val.slice(-1);
                            setOtp(newOtp);
                            if (index < 5) inputRefs.current[index + 1].focus();
                          } else {
                            const newOtp = [...otp];
                            newOtp[index] = '';
                            setOtp(newOtp);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !otp[index] && index > 0) {
                            inputRefs.current[index - 1].focus();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
                          if (pastedData) {
                            const newOtp = [...otp];
                            for (let i = 0; i < pastedData.length; i++) {
                              newOtp[i] = pastedData[i];
                            }
                            setOtp(newOtp);
                            const nextIndex = Math.min(pastedData.length, 5);
                            if (nextIndex < 6) {
                              inputRefs.current[nextIndex].focus();
                            } else {
                              inputRefs.current[5].focus();
                            }
                          }
                        }}
                        style={{ width: '48px', height: '56px', fontSize: '24px', textAlign: 'center', padding: '0', borderRadius: '12px' }}
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className="reg-submit-btn"
                  disabled={verifying || otp.join('').length !== 6 || success.includes('Redirecting')}
                >
                  {verifying ? (
                    <>
                      <span className="reg-spinner"></span>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check-circle"></i>
                      Verify OTP
                    </>
                  )}
                </button>

                <div className="reg-divider">
                  <div className="reg-divider-line"></div>
                  <span className="reg-divider-text">Didn't receive code?</span>
                  <div className="reg-divider-line"></div>
                </div>

                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <button 
                    type="button" 
                    onClick={handleResendOtp}
                    disabled={resending || verifying}
                    style={{ background: 'none', border: 'none', color: '#061830', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {resending ? 'Resending...' : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}

            {step === 'register' && (
              <>
                <div className="reg-divider">
                  <div className="reg-divider-line"></div>
                  <span className="reg-divider-text">Already have an account?</span>
                  <div className="reg-divider-line"></div>
                </div>

                <p className="reg-login-link">
                  <Link to="/login">Sign in to your account →</Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

/* ── Password Strength Sub-component ── */
const PasswordStrength = ({ password }) => {
  if (!password) return null;

  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;

  const colors = ['#f87171', '#fb923c', '#facc15', '#CFA047'];
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  const color = colors[score - 1] || '#e2e8f0';

  return (
    <div>
      <div className="reg-strength-bar">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="reg-strength-seg"
            style={{ background: i < score ? color : '#e2e8f0' }}
          />
        ))}
      </div>
      <div className="reg-strength-label" style={{ color }}>
        {labels[score - 1] || ''}
      </div>
    </div>
  );
};

export default RegisterPage;
